import { Box, Progress, Theme, useMediaQuery, useTheme } from "@carrotfertility/carotene-core"
import React from "react"
import CollectiveItemTile from "#/components/carrot-collective/CollectiveItemTile"
import useCollectiveItems from "#/components/carrot-collective/hooks/useCollectiveItems"
import DisclaimerTile from "#/components/carrot-collective/DisclaimerTile"
import ComingSoonTile from "#/components/carrot-collective/ComingSoonTile"
import { useContentfulConditionalGate } from "#/lib/contentful/components/ContentfulConditionGate"
import { useDoesUserSatisfyConditions } from "#/services/contentful/conditional-gate"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { ProgramType } from "#/lib/carrot-api/types/Benefit"

export default function CollectiveGrid(): JSX.Element {
  const { data: collectiveItems, isLoading: isLoadingItems, isError } = useCollectiveItems()
  const { data: benefit } = useBenefit()
  const allowConditionsOverride = true
  const isPro = benefit?.program?.type === ProgramType.PRO

  const { isLoading: isLoadingConditions } = useDoesUserSatisfyConditions(allowConditionsOverride)

  const collectiveItemTilesWithConditions = React.useMemo(
    () =>
      collectiveItems?.map((item) => ({
        element: <CollectiveItemTile key={item.sys.id} entry={item} isPro={isPro} />,
        conditions: item.fields.conditions
      })) ?? [],
    [collectiveItems, isPro]
  )

  const filteredCollectiveItemTiles = useContentfulConditionalGate(
    collectiveItemTilesWithConditions,
    allowConditionsOverride
  )

  if (isLoadingItems || isLoadingConditions) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Progress />
      </Box>
    )
  }

  if (isError || !collectiveItems || collectiveItems.length === 0) {
    return <ComingSoonTile />
  }

  return (
    <MasonryLayout>
      <DisclaimerTile isPro={isPro} />
      {filteredCollectiveItemTiles}
      <ComingSoonTile />
    </MasonryLayout>
  )
}

function MasonryLayout({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.between("sm", "lg"))

  const columnCount = isMobile ? 1 : isTablet ? 2 : 3
  const childrenArray = React.Children.toArray(children)
  const comingSoonTile = childrenArray[childrenArray.length - 1]
  const otherTiles = childrenArray.slice(0, -1)
  const lastRowItemCount = otherTiles.length % columnCount || columnCount
  const comingSoonColumnIndex = lastRowItemCount % columnCount

  const columns: React.ReactNode[][] = Array.from({ length: columnCount }, () => [])
  otherTiles.forEach((child, index) => {
    columns[index % columnCount].push(child)
  })

  columns[comingSoonColumnIndex].push(comingSoonTile)

  return (
    <Box
      sx={{
        display: "flex",
        gap: theme.spacing(theme.tokens.spacing.lg)
      }}
    >
      {columns.map((column, columnIndex) => (
        <Box
          key={columnIndex}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(theme.tokens.spacing.lg),
            flex: 1
          }}
        >
          {column}
        </Box>
      ))}
    </Box>
  )
}
